import { useCallback, useRef, useState } from "react";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  streaming?: boolean;
  sources?: Array<{
    chunk_id: string;
    doc_id: string;
    section_id: string | null;
    path: string;
    score: number;
    heading_path?: string[];
  }>;
  retrieval?: unknown;
}

interface SendArgs {
  question: string;
  scope: string;
  filters?: Record<string, unknown>;
  notebookId?: string;
  answerLang?: string;
}

function readSseEvent(rawEvent: string): { kind: string; data: string } | null {
  const lines = rawEvent.split(/\r?\n/);
  const eventLine = lines.find((line) => line.startsWith("event:"));
  const dataLines = lines
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart());
  if (dataLines.length === 0) return null;
  return {
    kind: eventLine?.slice(6).trim() || "token",
    data: dataLines.join("\n"),
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function extractVisibleToken(data: string): string {
  const trimmed = data.trim();
  if (!trimmed || trimmed === "[DONE]") return "";

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === "string") return parsed;
    if (!isObject(parsed)) return "";

    const choices = parsed.choices;
    if (Array.isArray(choices) && isObject(choices[0])) {
      const delta = choices[0].delta;
      if (isObject(delta) && typeof delta.content === "string") return delta.content;
    }
    return "";
  } catch {
    return trimmed.startsWith("{") || trimmed.startsWith("[") ? "" : data;
  }
}

/**
 * Streaming chat hook. Posts to /v2/ask with stream=true and parses SSE
 * events (`plan`, `retrieval`, `sources`, `token`, `done`) into the
 * current assistant message.
 */
export function useSseChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(async (args: SendArgs) => {
    setIsStreaming(true);
    const userId = `m-${Math.random().toString(36).slice(2, 9)}`;
    const assistantId = `m-${Math.random().toString(36).slice(2, 9)}`;
    setMessages((m) => [
      ...m,
      { id: userId, role: "user", content: args.question },
      { id: assistantId, role: "assistant", content: "", streaming: true },
    ]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const resp = await fetch("/v2/ask", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", accept: "text/event-stream" },
        body: JSON.stringify({
          question: args.question,
          scope: args.scope,
          filters: args.filters,
          notebook_id: args.notebookId,
          answer_lang: args.answerLang,
          stream: true,
          include_sources: true,
        }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`API ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE events separated by \n\n
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";
        for (const e of events) {
          const event = readSseEvent(e);
          if (!event) continue;
          const { data, kind } = event;
          if (kind === "sources") {
            try {
              const sources = JSON.parse(data) as ChatMessage["sources"];
              setMessages((m) =>
                m.map((msg) => (msg.id === assistantId ? { ...msg, sources } : msg)),
              );
            } catch {
              /* ignore */
            }
          } else if (kind === "retrieval") {
            try {
              const retrieval = JSON.parse(data);
              setMessages((m) =>
                m.map((msg) => (msg.id === assistantId ? { ...msg, retrieval } : msg)),
              );
            } catch {
              /* ignore */
            }
          } else if (kind === "done") {
            setMessages((m) =>
              m.map((msg) => (msg.id === assistantId ? { ...msg, streaming: false } : msg)),
            );
          } else if (kind === "plan") {
            // Plan metadata is useful for debugging but should not be rendered as assistant text.
          } else if (kind === "error") {
            setMessages((m) =>
              m.map((msg) =>
                msg.id === assistantId
                  ? { ...msg, streaming: false, content: `${msg.content}\n\n⚠️ ${data}` }
                  : msg,
              ),
            );
          } else {
            const token = extractVisibleToken(data);
            if (!token) continue;
            setMessages((m) =>
              m.map((msg) =>
                msg.id === assistantId ? { ...msg, content: msg.content + token } : msg,
              ),
            );
          }
        }
      }
    } catch (err) {
      setMessages((m) =>
        m.map((msg) =>
          msg.id === assistantId
            ? { ...msg, streaming: false, content: `⚠️ Conexión interrumpida: ${String(err)}` }
            : msg,
        ),
      );
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, []);

  function cancel(): void {
    abortRef.current?.abort();
    setIsStreaming(false);
  }

  function clear(): void {
    setMessages([]);
  }

  return { messages, isStreaming, send, cancel, clear };
}
